import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  unit: "pcs",
  opening_qty: 0,
  low_stock_threshold: 0,
  mapping_mode: "",
  room_pars: [],
};

export const LinenItemCreateForm = ({ roomTypes, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const updateRoom = (roomTypeId, checked) => {
    const room_pars = checked
      ? [...form.room_pars, { room_type_id: roomTypeId, par_qty: 0 }]
      : form.room_pars.filter(entry => entry.room_type_id !== roomTypeId);
    setForm({ ...form, room_pars });
  };

  const updatePar = (roomTypeId, value) => {
    const room_pars = form.room_pars.map(entry => (
      entry.room_type_id === roomTypeId
        ? { ...entry, par_qty: Number(value) || 0 }
        : entry
    ));
    setForm({ ...form, room_pars });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Linen item name is required");
      return;
    }
    if (!form.mapping_mode) {
      toast.error("Choose Room linen or Store-only linen");
      return;
    }
    if (form.mapping_mode === "room_linen" && !form.room_pars.length) {
      toast.error("Select at least one room type and PAR quantity");
      return;
    }
    setBusy(true);
    try {
      await api.post("/items", {
        ...form,
        name: form.name.trim(),
        opening_qty: Number(form.opening_qty) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
      });
      toast.success("Linen item created");
      setForm(emptyForm);
      onCreated();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="linen-item-create-form">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        <div className="md:col-span-2">
          <Label>Name</Label>
          <Input
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
            className="mt-1 h-11"
            data-testid="new-item-name"
          />
        </div>
        <div>
          <Label>Unit</Label>
          <Input
            value={form.unit}
            onChange={event => setForm({ ...form, unit: event.target.value })}
            className="mt-1 h-11"
            data-testid="new-item-unit"
          />
        </div>
        <div>
          <Label>Opening</Label>
          <Input
            type="number"
            value={form.opening_qty}
            onChange={event => setForm({ ...form, opening_qty: event.target.value })}
            className="mt-1 h-11"
            data-testid="new-item-opening"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <Label>Low threshold</Label>
          <Input
            type="number"
            value={form.low_stock_threshold}
            onChange={event => setForm({ ...form, low_stock_threshold: event.target.value })}
            className="mt-1 h-11"
            data-testid="new-item-low-threshold"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Linen use</Label>
          <Select
            value={form.mapping_mode}
            onValueChange={mapping_mode => setForm({ ...form, mapping_mode, room_pars: [] })}
          >
            <SelectTrigger className="mt-1 h-11" data-testid="new-item-mapping-mode">
              <SelectValue placeholder="Choose mapping" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="room_linen">Room linen</SelectItem>
              <SelectItem value="store_only">Store-only linen — No room mapping required</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {form.mapping_mode === "room_linen" && (
        <div className="border-t pt-4" data-testid="new-item-room-mappings">
          <div className="mb-2 text-sm font-semibold text-slate-800">Applicable room types</div>
          <div className="space-y-2">
            {roomTypes.filter(roomType => roomType.active !== false).map(roomType => {
              const entry = form.room_pars.find(item => item.room_type_id === roomType.id);
              return (
                <div
                  key={roomType.id}
                  className="grid grid-cols-[auto_1fr_7rem] items-center gap-3"
                >
                  <input
                    id={`new-item-room-${roomType.id}`}
                    type="checkbox"
                    checked={Boolean(entry)}
                    onChange={event => updateRoom(roomType.id, event.target.checked)}
                    data-testid={`new-item-room-toggle-${roomType.id}`}
                  />
                  <Label htmlFor={`new-item-room-${roomType.id}`} className="!mb-0">
                    {roomType.name}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={entry?.par_qty ?? ""}
                    disabled={!entry}
                    onChange={event => updatePar(roomType.id, event.target.value)}
                    placeholder="PAR"
                    data-testid={`new-item-par-${roomType.id}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Button
        onClick={save}
        disabled={busy}
        className="bg-slate-900 text-white hover:bg-slate-800"
        data-testid="add-item"
      >
        Add Item
      </Button>
    </div>
  );
};